import MidiEffect from '../MidiEffect.js';
import {getMidiEffect} from '../getMidiEffect.js';
import RackableEffect from '../../rack/RackableEffect.js';
import ConfigurableRack from '../../rack/ConfigurableRack.js';
import MacroMixin from '../../MacroMixin.js';
import {before, otherwise, when, after, ACCENT, DOUBLE, STACCATO, PIANISSIMO, PIANO, REST, rules} from '../scripting/trules.js';

import {
    Legato,
    Otherwise,
    Figure,
    Max,
    First,
    Last,
    Dash1,
    Dash2,
    Dot,
    Accent,
    Staccato,
    Piano,
    Flam,
    Regular,
    Any,
    Double,
    Times,
    Pianissimo,
    Rest} from 'effects/midi/jaki/RuleEffects.js';
import NodeMessaging from "messaging/NodeMessaging.js";
import {RULES} from "messaging/message-types.js";


export default class RulesRack extends NodeMessaging(RackableEffect(ConfigurableRack(MacroMixin(MidiEffect)))) {

    constructor(track, zObject) {
        super("Rules Rack", track);
        this.zObject = zObject;
        this.initializeConfigurations(this.adjacencyLists);
        this.addInitialRules();

        this.newOutlet(RULES);
    }

    addInitialRules() {
        this.loaded = false;
        this.addEffect(new Dot(this));
        this.addEffect(new Dash1(this));
        this.addEffect(new Dash2(this));
        this.addEffect(new Otherwise(this));
        this.addEffect(new Rest(this));
        this.addEffect(new Pianissimo(this));
        this.addEffect(new Piano(this));
        this.addEffect(new Staccato(this));
        this.addEffect(new Accent(this));
        this.addEffect(new Legato(this));
        this.addEffect(new Double(this));
        this.addEffect(new Flam(this));
        this.addEffect(new Regular(this));
        this.addEffect(new Any(this));
        this.addEffect(new Times(this));
        this.loaded = true;
        this.updateRules();

        this.coord(0, 36, 10);
        this.coord(1, 92, 10);
        this.coord(2, 144, 10);
        this.coord(3, 203, 10);

        this.coord(4, 11, 72);
        this.coord(5, 60, 72);
        this.coord(6, 130, 72);
        this.coord(7, 177, 72);
        this.coord(8, 236, 72);
        this.coord(9, 199, 129);
        this.coord(10, 12, 129);
        this.coord(11, 63, 129);
        this.coord(12, 128, 129);
        this.coord(12, 200, 129);
        this.coord(12, 230, 129);
    }

    coord(i, x, y) {
        this.effects[i].x = x;
        this.effects[i].y = y;
    }

    get isRack() {
        return true;
    }

    addEffect(effect) {
        super.addEffect(effect);

        effect.rack = this;

        this.updateRules();
    }

    updateRules() {
        if (!this.loaded) {
            // only update rules after we've loaded
            // to avoid calling this function 100s of times
            return;
        }
        // based on the configuration of the rack construct a rule

        // first start at the dashes and dots, going thru the outputs

        let ruleBook = [];
        let names = [
            "Dot",
            "Dash 1",
            "Dash 2",
            "Accent",
            "Any",
            "Times",
            "Double",
            "Staccato",
            "Legato",
            "Piano",
            "Rest",
            "Pianissimo",
            "Otherwise"];
        ruleBook = [];
        names.forEach(
            name => {
                ruleBook = [
                    ... ruleBook,
                    ... this.generateRules(
                        this.effects.find(n => n.name === name))];
            });


        this.rules = ruleBook;
        this.publishRules();
    }

    publishRules() {
        this.sendMessage(RULES, this.rules);
    }

    connectNodes(...args) {
        super.connectNodes(...args);
        this.updateRules();
    }

    deleteEdge(...args) {
        super.deleteEdge(...args);
        this.updateRules();
    }

    deleteEffect(...args) {
        super.deleteEffect(...args);
        this.updateRules();
    }

    generateRules(type, predicate) {
        if (!type) {
            return [];
        }
        let edges = this.adjacencyLists[type.id];
        if (!edges) {
            return [];
        }

        let _rules = [];
        for (let edge of edges) {
            let {effect, outputNumber} = edge;
            let _predicate = predicate;
            if (!predicate) {
                if (type.name === "Otherwise") {
                    _predicate = otherwise();
                } else {
                    _predicate = getPredicateFromNumber(outputNumber)(type.predicate || type.action);
                }
            }

            if (effect.aux) {
                // then need to branch out
                _predicate = effect.aux(_predicate, effect.value);
                _rules = [... _rules, ... this.generateRules(effect, _predicate)];
                continue;
            } else if (effect.action) {
                /*
                _predicate = effect.name === "Otherwise" ?
                    predicate.always(effect.action) : _predicate.decide(effect.action);
                    */
                _predicate = _predicate.always(effect.action);
            }
            _rules.push(_predicate);
        }
        return _rules;
    }

    get dot() {
        return this.effects.find(x => x.name === "Dot");
    }

    get dash1() {
        return this.effects.find(x => x.name === "Dash 1");
    }

    get dash2() {
        return this.effects.find(x => x.name === "Dash 2");
    }

    getJson() {
        return this.getRackJson();
    }

    fromJson(json, isCopy, x, isCopy2, isCopy3=isCopy) {
        this.adjacencyLists = {};
        this.initializeConfigurations(this.adjacencyLists, false);
        this.effects = [];
        this.loaded = false;
        let bulk = super.fromJson(json, isCopy);

        let {effects} = json;
        for (let i in effects) {
            let jsonEffect = effects[i];
            let effect = getMidiEffect(jsonEffect, this.track);
            bulk = {
                ... bulk,
                ... effect.fromJson(jsonEffect, isCopy3)
            };
            if (effect.id !== jsonEffect.id) {
                for (let config of json.configurations) {
                    let adj = config[jsonEffect.id];
                    config[effect.id] = adj;
                    delete config[jsonEffect.id];
                }
            }
            this.addEffect(effect, false);
            effect.x = jsonEffect._x || jsonEffect.x;
            effect.y = jsonEffect._y || jsonEffect.y;
        }

        this.fromRackJson(json, isCopy);
        this.loaded = true;
        // this.outputNode.id = 'speakers-' + this.id;
        this.updateRules();
        return bulk;
    }
}

const getPredicateFromNumber = (outputNumber) => {
    switch (outputNumber) {
    case 0:
        return before;
    case 1:
        return when;
    default:
        return after;
    }
};
